<template>
    <div :class="[colClass]">
        <div class="DFRRegionHeading" v-if="effectiveLabel">
            <h2 class="df-section-title">
                {{ effectiveLabel }}
                <LoadingWheel :width="20" v-if="loading" class="d-inline-block" />
            </h2>
        </div>
        <div class="clearfix" style="margin-bottom: 10px">
            <div>
                <div class="dfForEachItem clearfix" v-for="childObj in childrenObj" :key="childObj.child.vmId">
                    <div class="form-group col-md-12">
                        <ChildrenList :children-ids="childObj.childChildren" :view-only="isViewOnly"></ChildrenList>
                        <button type="button" class="btn btn-link dfNavButton dfNavRemoveButton" @click="removeItem(childObj.child)" v-if="(!isComputed || canAddRemoveItems) && !isViewOnly" :disabled="(settings.min != null && settings.min >= childrenObj.length) || (settings.minCalculationId != null && calculatedMin >= childrenObj.length)">
                            <span aria-hidden="true">&times;</span>
                            <span class="sr-only">{{ settings.removeText }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="form-group" v-if="(!isComputed || canAddRemoveItems) && !isViewOnly">
                <button type="button" class="btn btn-outline-secondary dfNavButton" @click="addItem" :disabled="(settings.max != null && settings.max <= childrenObj.length) || (settings.maxCalculationId != null && calculatedMax <= childrenObj.length)">
                    <span class="plus-sign" aria-hidden="true">+</span>
                    {{ settings.addText }}
                </button>
            </div>
        </div>
        <ErrorList :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, onUnmounted, watch, computed } from "vue";
//import { useMainStore } from "@/mainStore";
import { Observable } from "rxjs";
import ForEachDef, { ForEachDefItem } from "../elements/ForEachDef";
import { ForEachSettings } from "@cs-ts/for-each-settings";
import { useObservable } from "@vueuse/rxjs";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import arrayEquals from "@/lib/arrayEquals";
import { useMainStore } from "@/mainStore";

export default defineComponent({
    components: {
        LoadingWheel,
        ...components,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<ForEachDef, ForEachSettings>) {
        const mainStore = useMainStore();
        const base = setup<ForEachDef, ForEachSettings>(props);

        const loading = ref(false);
        const computedSubscription = ref();
        const computedMinSubscription = ref();
        const computedMaxSubscription = ref();
        const oldIds = ref();
        const calculatedIds = ref<string[]>([]);
        const calculatedMin = ref<number>();
        const calculatedMax = ref<number>();
        const effectiveLabel = useObservable(props.data.effectiveLabel$());

        const isComputed = computed(() => {
            return props.data.definition.settings.calculationId != null;
        });
        const canAddRemoveItems = computed(() => {
            return props.data.definition.settings.addRemoveItems;
        });
        const childrenObj = computed(() => {
            return (props.data.children as ForEachDefItem[])
                .filter(x => x.removedChild == undefined || x.removedChild == false)
                .map(id => base.pageStore.getElementByVmId(id))
                .map(child => ({
                    child,
                    childChildren: base.pageStore.getChildrenByVmId(child!.vmId)!.filter(el => base.elementFilter(el)),
                }));
        });
        const computedItems = computed(() => {
            if (!isComputed.value) {
                return null;
            }
            return props.data.resolveCalculation(props.data.definition.settings.calculationId!);
        });
        const computedMinItems = computed(() => {
            if (props.data.definition.settings.minCalculationId == null) {
                return null;
            }
            return props.data.resolveCalculation(props.data.definition.settings.minCalculationId);
        });
        const computedMaxItems = computed(() => {
            if (props.data.definition.settings.maxCalculationId == null) {
                return null;
            }
            return props.data.resolveCalculation(props.data.definition.settings.maxCalculationId);
        });

        const subscribeToComputedItems = (computedItems$: Observable<string[]>) => {
            if (computedSubscription.value != null) {
                computedSubscription.value.unsubscribe();
            }
            computedSubscription.value = computedItems$.subscribe(items => {
                //debugger;
                calculatedIds.value = items;
                setCalculatedItems(items);
            });
        };
        const subscribeToComputedMinItems = (computedMinItems$: Observable<string[]>) => {
            if (computedMinSubscription.value != null) {
                computedMinSubscription.value.unsubscribe();
            }
            computedMinSubscription.value = computedMinItems$.subscribe(items => {
                //debugger;
                if (items.length > 0) {
                    calculatedMin.value = parseInt(items[0]);
                }
            });
        };
        const subscribeToComputedMaxItems = (computedMaxItems$: Observable<string[]>) => {
            if (computedMaxSubscription.value != null) {
                computedMaxSubscription.value.unsubscribe();
            }
            computedMaxSubscription.value = computedMaxItems$.subscribe(items => {
                //debugger;
                if (items.length > 0) {
                    calculatedMax.value = parseInt(items[0]);
                }
            });
        };
        const addItem = () => {
            base.pageStore.addForEachItem(base.vmId.value);
            mainStore.setLiveRegion(Promise.resolve("Item has been added successfully."));
        };
        const removeItem = (item: ForEachDefItem) => {
            base.pageStore.removeForEachItem({ forEachVmId: base.vmId.value, item: item, calculatedItems: calculatedIds.value });
            mainStore.setLiveRegion(Promise.resolve("Item has been deleted successfully."));
        };
        const setCalculatedItems = async (newIds: string[]) => {
            if (newIds == null || arrayEquals(newIds, oldIds.value)) {
                return;
            }
            if (oldIds.value != null) {
                // Don't show loading on first load
                // Fake loading for UX since we don't know when real loading started
                loading.value = true;
                setTimeout(() => {
                    loading.value = false;
                }, 400);
            }
            oldIds.value = newIds;
            //if (base.pageStore.getValueByVmId.value == null) {
            //    // Is this a typo?
            //    return null;
            //}
            base.pageStore.setForEachChildren({ forEachVmId: base.vmId.value, ids: newIds });
        };

        const computedItemsV = computedItems.value;
        const computedMinItemsV = computedMinItems.value;
        const computedMaxItemsV = computedMaxItems.value;
        if (computedItemsV != null) {
            subscribeToComputedItems(computedItemsV);
        }
        if (computedMinItemsV != null) {
            subscribeToComputedMinItems(computedMinItemsV);
        }
        if (computedMaxItemsV != null) {
            subscribeToComputedMaxItems(computedMaxItemsV);
        }

        watch(computedItems, newComputedItems$ => {
            subscribeToComputedItems(newComputedItems$!);
        });
        watch(computedMinItems, newComputedMinItems$ => {
            subscribeToComputedMinItems(newComputedMinItems$!);
        });
        watch(computedMaxItems, newComputedMaxItems$ => {
            subscribeToComputedMaxItems(newComputedMaxItems$!);
        });

        onUnmounted(() => {
            if (computedSubscription.value != null) {
                computedSubscription.value.unsubscribe();
            }
            if (computedMinSubscription.value != null) {
                computedMinSubscription.value.unsubscribe();
            }
            if (computedMaxSubscription.value != null) {
                computedMaxSubscription.value.unsubscribe();
            }
        });

        return {
            loading,
            effectiveLabel,
            isComputed,
            childrenObj,
            canAddRemoveItems,
            calculatedMin,
            calculatedMax,
            addItem,
            removeItem,

            ...base.expose,
        };
    },
});
</script>
